import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useUploadJobs } from '../../context/UploadJobsContext';
import { useRBAC } from '../../auth/RBACProvider';
import apiService from '../../services/api';
import logger from '../../utils/logger';

const COMPLETION_STATUSES = new Set(['completed', 'failed']);
const VALIDATION_COMPLETION_STATUSES = new Set(['completed', 'failed', 'cancelled']);
const ACTIVE_STATUSES = new Set(['queued', 'processing', 'uploading', 'preparing', 'ready', 'verifying']);
type NotificationKind = 'upload' | 'lead-upload' | 'validation' | 'linkedin-sync';
const TYPE_COPY: Record<NotificationKind, string> = {
  upload: 'Prospects Upload',
  'lead-upload': 'Leads Upload',
  validation: 'Email Validation',
  'linkedin-sync': 'LinkedIn Sync',
};

const formatRelativeTime = (value?: string) => {
  if (!value) return 'moments ago';
  const target = new Date(value).getTime();
  if (Number.isNaN(target)) return value;
  const diff = Date.now() - target;
  if (diff < 60_000) return 'moments ago';
  if (diff < 3_600_000) {
    const minutes = Math.floor(diff / 60_000);
    return `${minutes}m ago`;
  }
  if (diff < 86_400_000) {
    const hours = Math.floor(diff / 3_600_000);
    return `${hours}h ago`;
  }
  const days = Math.floor(diff / 86_400_000);
  return days === 1 ? '1 day ago' : `${days}d ago`;
};

const statusCopy: Record<string, string> = {
  queued: 'Queued',
  processing: 'Processing',
  uploading: 'Uploading',
  preparing: 'Preparing',
  ready: 'Ready',
  verifying: 'Verifying',
  completed: 'Completed',
  failed: 'Failed',
  cancelled: 'Cancelled',
};

const statusIcon = (status: string) => {
  if (status === 'failed') return <ErrorIcon fontSize="small" />;
  if (status === 'completed') return <CheckCircleIcon fontSize="small" />;
  if (status === 'queued') return <HourglassBottomIcon fontSize="small" />;
  return <HourglassBottomIcon fontSize="small" />;
};

const normalizeStatus = (value?: string | null) => (value ?? 'queued').toString().toLowerCase();

type NotificationCard = {
  id: string;
  kind: NotificationKind;
  status: string;
  acknowledged: boolean;
  title: string;
  subtitle: string;
  message?: string | null;
  progress?: number;
  tally?: {
    value: number;
    total?: number;
    label: string;
  };
  timestamp?: string;
  deliverableCount?: number;
  undeliverableCount?: number;
  acceptAllCount?: number;
  unknownCount?: number;
  missingStats?: number;
};

const UploadNotificationBell = () => {
  const {
    uploads,
    leadUploads,
    validationJobs,
    linkedinSyncJobs,
    expiringCredentials,
    unseenCount,
    isPolling,
    refreshNotifications,
    acknowledgeUpload,
    acknowledgeLeadUpload,
    acknowledgeValidationJob,
    acknowledgeLinkedInSyncJob,
  } = useUploadJobs();
  const { hasPermission } = useRBAC();
  const [open, setOpen] = useState(false);
  const [pendingUserCount, setPendingUserCount] = useState(0);
  const [pendingUsersDismissed, setPendingUsersDismissed] = useState(false);
  const [credentialsDismissed, setCredentialsDismissed] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Check if user is admin/super_admin
  const isAdmin = hasPermission('user_management', 'update');

  // Fetch pending user count for admins
  const fetchPendingUsers = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const response = await apiService.getPendingUserCount();
      // Reset dismissed state if count increases (new pending users)
      if (response.count > pendingUserCount) {
        setPendingUsersDismissed(false);
      }
      setPendingUserCount(response.count);
    } catch (error) {
      logger.error('Failed to fetch pending user count', error);
    }
  }, [isAdmin, pendingUserCount]);

  useEffect(() => {
    fetchPendingUsers();
    // Poll for pending users every 60 seconds
    const interval = setInterval(fetchPendingUsers, 60000);
    return () => clearInterval(interval);
  }, [fetchPendingUsers]);

  // Total badge count including pending users and expiring credentials (only if not dismissed)
  const criticalCredentialCount = expiringCredentials.filter(c => {
    const days = c.minimum_days_remaining;
    return days !== null && days !== undefined && days < 7;
  }).length;
  
  const totalBadgeCount = unseenCount + 
    (isAdmin && !pendingUsersDismissed ? pendingUserCount : 0) +
    (isAdmin && !credentialsDismissed ? criticalCredentialCount : 0);

  const notifications: NotificationCard[] = useMemo(() => {
    const uploadItems: NotificationCard[] = uploads.map((job) => ({
      id: job._id,
      kind: 'upload',
      status: normalizeStatus(job.status),
      acknowledged: Boolean(job.acknowledged_at),
      title: job.filename || 'Prospects Upload',
      subtitle: job.account || 'General workspace',
      message: job.message,
      progress: job.progress,
      tally:
        typeof job.total_successful === 'number'
          ? {
              value: job.total_successful,
              total: job.total_processed ?? job.total_successful,
              label: 'records',
            }
          : undefined,
      timestamp: job.updated_at ?? job.created_at,
    }));

    const validationItems: NotificationCard[] = validationJobs.map((job) => ({
      id: job._id,
      kind: 'validation',
      status: normalizeStatus(job.status),
      acknowledged: Boolean(job.acknowledged_at),
      title: job.account ? `${job.account} validation` : 'Email validation job',
      subtitle:
        job.account_scope === 'all'
          ? 'All accounts'
          : job.account_scope === 'accessible'
          ? 'Accessible accounts'
          : job.account || 'Multiple accounts',
      message: job.message,
      progress: job.progress,
      tally:
        typeof job.processed_count === 'number'
          ? {
              value: job.processed_count,
              total: job.requested_count ?? job.processed_count,
              label: 'processed',
            }
          : undefined,
      timestamp: job.updated_at ?? job.created_at,
      deliverableCount: job.deliverable_count,
      undeliverableCount: job.undeliverable_count,
      acceptAllCount: job.accept_all_count,
      unknownCount: job.unknown_count,
    }));

    const leadUploadItems: NotificationCard[] = leadUploads.map((job) => ({
      id: job._id,
      kind: 'lead-upload',
      status: normalizeStatus(job.status),
      acknowledged: Boolean(job.acknowledged_at),
      title: job.filename || 'Leads Upload',
      subtitle: job.account || 'General workspace',
      message: job.message,
      progress: job.progress,
      tally:
        typeof job.total_successful === 'number'
          ? {
              value: job.total_successful,
              total: job.total_processed ?? job.total_successful,
              label: 'rows',
            }
          : undefined,
      timestamp: job.updated_at ?? job.created_at,
    }));

    const linkedinItems: NotificationCard[] = linkedinSyncJobs.map((job) => ({
      id: job._id,
      kind: 'linkedin-sync',
      status: normalizeStatus(job.status),
      acknowledged: Boolean(job.acknowledged_at),
      title: job.job_type === 'organic_posts' ? 'Organic posts sync' : job.job_type || 'LinkedIn sync job',
      subtitle: job.account || 'LinkedIn',
      message: job.message,
      progress: typeof job.progress === 'number' ? job.progress : undefined,
      tally:
        typeof job.posts_stored === 'number'
          ? {
              value: job.posts_stored ?? 0,
              total: job.posts_processed ?? job.total_posts_fetched ?? job.posts_stored ?? 0,
              label: 'stored',
            }
          : undefined,
      timestamp: job.updated_at ?? job.created_at,
      missingStats: job.posts_missing_stats ?? undefined,
    }));

    return [...uploadItems, ...leadUploadItems, ...validationItems, ...linkedinItems].sort((a, b) => {
      const left = new Date(b.timestamp ?? '').getTime();
      const right = new Date(a.timestamp ?? '').getTime();
      return Number.isNaN(left) || Number.isNaN(right) ? 0 : left - right;
    });
  }, [uploads, leadUploads, validationJobs, linkedinSyncJobs]);

  useEffect(() => {
    const handler = (event: MouseEvent) => {
      if (open && containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  useEffect(() => {
    const handleExternalOpen = () => {
      setOpen(true);
      refreshNotifications();
    };
    window.addEventListener('upload-bell:open', handleExternalOpen);
    return () => window.removeEventListener('upload-bell:open', handleExternalOpen);
  }, [refreshNotifications]);

  const handleToggle = () => {
    const next = !open;
    setOpen(next);
    if (next) {
      refreshNotifications();
      fetchPendingUsers();
    }
  };

  const handleAcknowledge = async (jobId: string, kind: NotificationKind) => {
    try {
      if (kind === 'upload') {
        await acknowledgeUpload(jobId);
      } else if (kind === 'lead-upload') {
        await acknowledgeLeadUpload(jobId);
      } else if (kind === 'validation') {
        await acknowledgeValidationJob(jobId);
      } else {
        await acknowledgeLinkedInSyncJob(jobId);
      }
    } catch (error) {
      logger.error('Unable to acknowledge upload job', error);
    }
  };

  return (
    <div className="upload-bell" ref={containerRef}>
      <button
        type="button"
        className={`upload-bell-button ${open ? 'active' : ''}`}
        onClick={handleToggle}
        aria-label="Upload notifications"
      >
        <NotificationsNoneIcon fontSize="medium" />
        {totalBadgeCount > 0 && <span className="upload-bell-badge">{totalBadgeCount}</span>}
        {isPolling && <span className="upload-bell-spinner" />}
      </button>

      {open && (
        <div className="upload-bell-menu">
          <div className="upload-bell-menu__header">
            <div>
              <p className="title">Notifications</p>
              <p className="subtitle">Job notifications and pending approvals.</p>
            </div>
            <button type="button" className="text-link" onClick={() => { refreshNotifications(); fetchPendingUsers(); }}>
              Refresh
            </button>
          </div>

          {/* Pending Users Section for Admins */}
          {isAdmin && pendingUserCount > 0 && !pendingUsersDismissed && (
            <div className="pending-users-section">
              <div className="pending-users-card">
                <PersonAddIcon fontSize="medium" className="pending-icon" />
                <div className="pending-users-info">
                  <p className="pending-title">{pendingUserCount} pending user{pendingUserCount > 1 ? 's' : ''}</p>
                  <p className="pending-subtitle">External users awaiting approval</p>
                </div>
                <div className="pending-users-actions">
                  <a href="#" className="text-link" onClick={(e) => { 
                    e.preventDefault(); 
                    setOpen(false);
                    // Navigate to user management - dispatch custom event
                    window.dispatchEvent(new CustomEvent('navigate', { detail: 'user-management' }));
                  }}>
                    Review
                  </a>
                  <button 
                    type="button" 
                    className="text-link dismiss-btn" 
                    onClick={() => setPendingUsersDismissed(true)}
                    title="Dismiss this notification (will reappear on refresh)"
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Expiring Credentials Section for Admins */}
          {isAdmin && expiringCredentials.length > 0 && !credentialsDismissed && (
            <div className="pending-users-section">
              <div className="pending-users-card credential-expiry-card">
                <WarningAmberIcon fontSize="medium" className="pending-icon warning-icon" />
                <div className="pending-users-info">
                  <p className="pending-title">
                    {criticalCredentialCount > 0 
                      ? `${criticalCredentialCount} credential${criticalCredentialCount > 1 ? 's' : ''} expiring soon`
                      : `${expiringCredentials.length} credential${expiringCredentials.length > 1 ? 's' : ''} expiring`
                    }
                  </p>
                  <p className="pending-subtitle">
                    {criticalCredentialCount > 0 
                      ? `Critical: Less than 7 days remaining`
                      : `Expiring within 30 days`
                    }
                  </p>
                  {expiringCredentials.slice(0, 3).map((cred, idx) => {
                    const days = cred.minimum_days_remaining ?? 0;
                    const urgencyClass = days < 0 ? 'expired' : days < 7 ? 'critical' : 'warning';
                    return (
                      <p key={idx} className={`credential-item ${urgencyClass}`}>
                        {cred.channel} - {cred.account || 'Unknown'}: {days < 0 ? 'Expired' : `${days}d left`}
                      </p>
                    );
                  })}
                  {expiringCredentials.length > 3 && (
                    <p className="credential-item">+{expiringCredentials.length - 3} more...</p>
                  )}
                </div>
                <div className="pending-users-actions">
                  <a href="#" className="text-link" onClick={(e) => { 
                    e.preventDefault(); 
                    setOpen(false);
                    // Navigate to account management credentials
                    window.dispatchEvent(new CustomEvent('navigate', { detail: 'account-management' }));
                  }}>
                    Manage
                  </a>
                  <button 
                    type="button" 
                    className="text-link dismiss-btn" 
                    onClick={() => setCredentialsDismissed(true)}
                    title="Dismiss this notification (will reappear on refresh)"
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            </div>
          )}

          {notifications.length === 0 && pendingUserCount === 0 && expiringCredentials.length === 0 ? (
            <div className="upload-bell-empty">
              <DoneAllIcon fontSize="large" />
              <p>No notifications at this time.</p>
            </div>
          ) : notifications.length === 0 ? (
            // Only pending users/credentials, no job notifications - don't show empty state
            null
          ) : (
            <ul className="upload-bell-list">
              {notifications.map((item) => {
                const displayStatus = statusCopy[item.status] ?? item.status;
                const isUpload = item.kind === 'upload';
                const isLinkedInSync = item.kind === 'linkedin-sync';
                const isFinalStatus = isUpload
                  ? COMPLETION_STATUSES.has(item.status)
                  : isLinkedInSync
                  ? COMPLETION_STATUSES.has(item.status)
                  : VALIDATION_COMPLETION_STATUSES.has(item.status);
                const showAcknowledge = isFinalStatus && !item.acknowledged;
                const statusClass = ACTIVE_STATUSES.has(item.status)
                  ? 'status-active'
                  : item.status === 'failed' || item.status === 'cancelled'
                  ? 'status-failed'
                  : 'status-complete';
                const progressValue = typeof item.progress === 'number' ? Math.round(item.progress) : undefined;
                const hasBreakdown =
                  item.kind === 'validation' &&
                  ((item.deliverableCount ?? 0) > 0 ||
                    (item.undeliverableCount ?? 0) > 0 ||
                    (item.acceptAllCount ?? 0) > 0 ||
                    (item.unknownCount ?? 0) > 0);

                return (
                  <li key={`${item.kind}-${item.id}`} className="upload-bell-card">
                    <div className="upload-bell-card__status">
                      <span className={`status-chip ${statusClass}`}>
                        {statusIcon(item.status)}
                        <span>{displayStatus}</span>
                      </span>
                      <span className="upload-bell-card__time">{formatRelativeTime(item.timestamp)}</span>
                    </div>

                    <div className="upload-bell-card__meta">
                      <div className="upload-bell-type-row">
                        <span className={`type-chip type-${item.kind}`}>{TYPE_COPY[item.kind]}</span>
                      </div>
                      <p className="filename" title={item.title}>{item.title}</p>
                      <p className="account">{item.subtitle}</p>
                      {item.message && <p className="message">{item.message}</p>}
                      {item.kind === 'linkedin-sync' && typeof item.missingStats === 'number' && item.missingStats > 0 && (
                        <p className="message subtle">Missing stats: {item.missingStats}</p>
                      )}
                      {hasBreakdown && (
                        <div className="validation-breakdown">
                          <span>D {item.deliverableCount ?? 0}</span>
                          <span>U {item.undeliverableCount ?? 0}</span>
                          <span>AA {item.acceptAllCount ?? 0}</span>
                          <span>Unk {item.unknownCount ?? 0}</span>
                        </div>
                      )}
                    </div>

                    <div className="upload-bell-card__footer">
                      <div className="progress">
                        <span>{progressValue !== undefined ? `${progressValue}%` : '—'}</span>
                        {item.tally && (
                          <span>
                            {item.tally.value}/{item.tally.total ?? item.tally.value} {item.tally.label}
                          </span>
                        )}
                      </div>
                      {showAcknowledge && (
                        <button
                          type="button"
                          className="text-link"
                          onClick={() => handleAcknowledge(item.id, item.kind)}
                        >
                          Mark as read
                        </button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default UploadNotificationBell;
